import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { VolumeApi } from "@/lib/volume-api";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { cn, COMMON_FETCH_HEADERS } from "@/lib/utils";
import { Info } from "lucide-react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod/v4";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { env } from "@/env";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { hasRole } from "@/lib/auth";
import { useSelectedApplication } from "@/hooks/use-selected-application";
import { CardWithHeader } from "@/components/card-with-header";
import { Separator } from "@/components/ui/separator";

export const Route = createFileRoute(
  "/(app)/_main-layout/_settings/settings/webhooks-callbacks",
)({
  component: () => <WebhooksCallbacks />,
  beforeLoad({ context }) {
    if (!hasRole("EDIT_SETTINGS", context.authorities)) {
      throw redirect({ to: "/settings/account-settings", search: true });
    }
  },
  loader: ({ context: { accessToken, merchantId, selectedApplicationId } }) => {
    const api = new VolumeApi(accessToken, selectedApplicationId);
    return api.getMerchant({ merchantId });
  },
});

type WebhookCallbackType =
  | "merchant-callback-url-for-mobile"
  | "merchant-callback-url-for-web"
  | "webhook"
  | "refund-webhook"
  | "payout-webhook"
  | "cancel-url";

function WebhooksCallbacks() {
  const { merchantId, accessToken } = Route.useRouteContext();
  const selectedApplication = useSelectedApplication();

  return (
    <div className="space-y-6">
      <CardWithHeader title="Webhook URLs">
        <div className="space-y-0">
          <Row
            text="Payment status webhook URL"
            value={selectedApplication.paymentStatusWebhookUrl}
            webhookOrCallbackType={"webhook"}
            accessToken={accessToken}
            merchantId={merchantId}
            applicationId={selectedApplication.applicationId}
            roundedBottom={!selectedApplication.hasVirtualAccountsEnabled}
          />
          {selectedApplication.hasVirtualAccountsEnabled ? (
            <>
              <Row
                text="Refund status webhook URL"
                value={selectedApplication.refundWebhookUrl}
                webhookOrCallbackType={"refund-webhook"}
                accessToken={accessToken}
                merchantId={merchantId}
                applicationId={selectedApplication.applicationId}
              />
              <Row
                text="Payout status webhook URL"
                value={selectedApplication.payoutWebhookUrl}
                webhookOrCallbackType={"payout-webhook"}
                accessToken={accessToken}
                merchantId={merchantId}
                applicationId={selectedApplication.applicationId}
                roundedBottom
              />
            </>
          ) : null}
        </div>
      </CardWithHeader>

      <CardWithHeader title="Callback URLs">
        <div className="space-y-0">
          <Row
            text="Callback URL for mobile"
            value={selectedApplication.merchantCallbackUrlForMobile}
            webhookOrCallbackType={"merchant-callback-url-for-mobile"}
            accessToken={accessToken}
            merchantId={merchantId}
            applicationId={selectedApplication.applicationId}
            isOptional={
              selectedApplication.merchantCallbackUrlForWeb !== null ||
              selectedApplication.merchantCallbackUrlForMobile !== null
            }
          />
          <Row
            text="Callback URL for web"
            value={selectedApplication.merchantCallbackUrlForWeb}
            webhookOrCallbackType={"merchant-callback-url-for-web"}
            accessToken={accessToken}
            merchantId={merchantId}
            applicationId={selectedApplication.applicationId}
            isOptional={
              selectedApplication.merchantCallbackUrlForWeb !== null ||
              selectedApplication.merchantCallbackUrlForMobile !== null
            }
          />

          <Row
            text="Cancel URL"
            value={selectedApplication.cancelUrl}
            webhookOrCallbackType={"cancel-url"}
            accessToken={accessToken}
            merchantId={merchantId}
            applicationId={selectedApplication.applicationId}
            isOptional
            roundedBottom
          />
        </div>
      </CardWithHeader>

      <div className="space-y-2">
        <p className="text-V_Danger30">* mandatory</p>
        <p className="text-V_Danger30">
          ** at least one callback URL is required
        </p>
      </div>
    </div>
  );
}

function Row({
  text,
  value,
  accessToken,
  merchantId,
  applicationId,
  webhookOrCallbackType,
  isOptional = false,
  roundedBottom = false,
}: {
  text: string;
  value?: string | null;
  webhookOrCallbackType: WebhookCallbackType;
  accessToken: string;
  merchantId: string;
  applicationId: string;
  roundedBottom?: boolean;
  isOptional?: boolean;
}) {
  const docsLink = () => {
    switch (webhookOrCallbackType) {
      case "cancel-url":
        return "https://docs.getvolume.com/hosted-checkout#cancel-button";
      case "payout-webhook":
        return "https://docs.getvolume.com/virtual-accounts/Payouts";
      case "merchant-callback-url-for-web":
        return "https://docs.getvolume.com/single-payment/merchant-callback";
      case "merchant-callback-url-for-mobile":
        return "https://docs.getvolume.com/single-payment/merchant-callback#implementing-the-after-payment-screen-callback-in-mobile-app";
      case "webhook":
      case "refund-webhook":
        return "https://docs.getvolume.com/single-payment/webhooks";
    }
  };
  const docsLinkText = () => {
    switch (webhookOrCallbackType) {
      case "cancel-url":
        return "Cancel URL docs";
      case "payout-webhook":
        return "Payout Webhook docs";
      case "merchant-callback-url-for-web":
        return "Callback URL for Web docs";
      case "merchant-callback-url-for-mobile":
        return "Callback URL for Mobile docs";
      case "webhook":
        return "Payment Status Webhook docs";
      case "refund-webhook":
        return "Refund Webhook docs";
    }
  };
  const docsHref = docsLink();
  return (
    <>
      <div className="md:flex gap-4 text-sm items-center min-w-[400px]">
        <TooltipProvider>
          <Tooltip delayDuration={0}>
            <TooltipTrigger className="flex gap-2 items-center min-w-[300px]">
              <Info className="mr-4 mt-0.5" height={18} />
              <div className="flex mt-1">
                <div className="v-paragraph-bold">{text}</div>
                {!isOptional &&
                webhookOrCallbackType !== "merchant-callback-url-for-web" &&
                webhookOrCallbackType !== "merchant-callback-url-for-mobile" ? (
                  <div className="text-red-500 ml-2 text-lg">*</div>
                ) : null}
                {webhookOrCallbackType === "merchant-callback-url-for-web" ||
                webhookOrCallbackType === "merchant-callback-url-for-mobile" ? (
                  <div className="text-red-500 ml-2 text-lg">**</div>
                ) : null}
              </div>
            </TooltipTrigger>
            <PutBadge webhookCallbackType={webhookOrCallbackType} />
            <TooltipContent
              className="p-2 px-3 border-0 -mt-1"
              align="start"
              alignOffset={46}
              side="bottom"
            >
              <p>
                <a
                  href={docsHref}
                  className="underline hover:cursor-pointer text-volume-text-purple"
                  target="_blank rel=noopener noreferrer"
                >
                  {docsLinkText()}
                </a>
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <div className={cn(!isOptional && !value && "text-red-500")}>
          {isOptional && !value
            ? null
            : (value ?? (
                <div className="v-paragraph-bold" style={{ color: "#B92624" }}>
                  Missing value, please add URL.
                </div>
              ))}
        </div>
        <div className="ml-auto font-extrabold text-volume-text-purple">
          <WebhooksCallbacksDialog
            webhookOrCallbackType={webhookOrCallbackType}
            accessToken={accessToken}
            merchantId={merchantId}
            applicationId={applicationId}
            currentValue={value ?? ""}
          />
        </div>
      </div>
      {!roundedBottom && <Separator className="my-4" />}
    </>
  );
}

function PutBadge({
  webhookCallbackType,
}: {
  webhookCallbackType: WebhookCallbackType;
}) {
  if (webhookCallbackType.includes("webhook")) {
    return (
      <div className="flex mr-10">
        <Badge className="bg-[#FCF1D9] select-none text-V_Warning30 hover:bg-[#FCF1D9]">
          <p>PUT</p>
        </Badge>
      </div>
    );
  }
  return <div className="min-w-[90px]"></div>;
}

const FormSchema = z.object({
  url: z.string().url({ message: "Please enter a valid URL" }),
});
const FormSchemaCancelUrl = z.object({
  url: z
    .string()
    .url({ message: "Please enter a valid URL" })
    .or(z.literal("")),
});

const webhookOrCallbackTypeToTitle = {
  "merchant-callback-url-for-mobile": "Callback url for mobile",
  "merchant-callback-url-for-web": "Callback url for web",
  webhook: "Payment status webhook url",
  "refund-webhook": "Refund webhook url",
  "payout-webhook": "Payout webhook url",
  "cancel-url": "Cancel url",
} as const;
const webhookOrCallbackTypeToRequestBodyKey = {
  "merchant-callback-url-for-mobile": "merchantCallbackUrlForMobile",
  "merchant-callback-url-for-web": "merchantCallbackUrlForWeb",
  webhook: "webhookUrl",
  "refund-webhook": "webhookUrl",
  "payout-webhook": "webhookUrl",
  "cancel-url": "cancelUrl",
} as const;

function WebhooksCallbacksDialog({
  webhookOrCallbackType,
  accessToken,
  merchantId,
  applicationId,
  currentValue,
}: {
  webhookOrCallbackType:
    | "merchant-callback-url-for-mobile"
    | "merchant-callback-url-for-web"
    | "webhook"
    | "refund-webhook"
    | "payout-webhook"
    | "cancel-url";
  accessToken: string;
  merchantId: string;
  applicationId: string;
  currentValue: string;
}) {
  const [open, setOpen] = useState(false);
  const router = useRouter();

  const form = useForm({
    defaultValues: {
      url: currentValue,
    },
    resolver: zodResolver(
      webhookOrCallbackType === "cancel-url" ? FormSchemaCancelUrl : FormSchema,
    ),
  });

  // Get the current form value to compare with the original value
  const currentFormValue = form.watch("url");
  const isValueUnchanged = currentFormValue === currentValue;

  const mutation = useMutation({
    mutationKey: [`update-${webhookOrCallbackType}`],
    mutationFn: async (url: string) => {
      return toast.promise(
        fetch(
          `${env.VITE_VOLUME_URL}/api/merchants/${merchantId}/applications/${applicationId}/${webhookOrCallbackType}`,
          {
            method: "PUT",
            body: JSON.stringify({
              applicationId,
              merchantId,
              [webhookOrCallbackTypeToRequestBodyKey[webhookOrCallbackType]]:
                url,
            }),
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${accessToken}`,
              "x-application-id": applicationId,
              ...COMMON_FETCH_HEADERS,
            },
          },
        ).then((response) => {
          if (!response.ok) throw new Error(response.statusText);
          return response.json() as object;
        }),
        {
          success: "URL updated successfully",
          error: "There was an error updating the URL",
          loading: "Please wait",
        },
      );
    },
    onSuccess: async () => {
      await new Promise((resolve) => setTimeout(resolve, 1000)); // Give backend time to update. Maybe instead we show the entered value and allow the data in background to refresh?
      await router.invalidate();
    },
    onSettled: () => {
      setOpen(false);
    },
  });

  function onSubmit(data: z.infer<typeof FormSchema>) {
    mutation.mutate(data.url);
  }
  return (
    <Dialog
      open={open}
      onOpenChange={(value) => {
        setOpen(value);
      }}
    >
      <DialogTrigger asChild>
        <div
          className="v-default-buttons hover:cursor-pointer hover:underline"
          onClick={() => {
            setOpen(true);
          }}
        >
          Edit
        </div>
      </DialogTrigger>
      <DialogContent className=" max-w-3xl" aria-describedby={undefined}>
        <DialogTitle>
          {webhookOrCallbackType.includes("webhook")
            ? "Edit Webhook URL"
            : "Edit Callback URL"}
        </DialogTitle>
        <Form {...form}>
          <form
            onSubmit={(event) => void form.handleSubmit(onSubmit)(event)}
            className=" space-y-6"
          >
            <FormField
              control={form.control}
              name="url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {webhookOrCallbackTypeToTitle[webhookOrCallbackType]}
                  </FormLabel>
                  <FormControl>
                    <Input className="w-full" type="url" {...field} />
                  </FormControl>

                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="w-full"
              disabled={mutation.isPending || isValueUnchanged}
            >
              Submit
            </Button>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
